
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

// Interfaces
interface CustomerDetails {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  orders_count?: number;
  total_spent?: number;
}

export const useCustomerDetails = (customerId: string | null) => {
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch customer details when customerId changes
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!customerId) {
        setCustomerDetails(null);
        return;
      }

      setLoading(true);
      try {
        // Fetch customer details from the database
        const { data: customerData, error } = await supabase
          .from('customers')
          .select('id, name, email, phone, address')
          .eq('id', customerId)
          .single();

        if (error) {
          throw error;
        }

        // Get order stats
        const { data: orders, error: ordersError } = await supabase
          .from('orders')
          .select('total_amount')
          .eq('customer_id', customerId);

        if (ordersError) {
          console.error('Error fetching customer orders:', ordersError);
        }

        // Calculate stats
        const ordersCount = orders?.length || 0;
        const totalSpent = orders?.reduce((sum, order) => sum + (parseFloat(order.total_amount) || 0), 0) || 0;

        // Set customer details with stats
        setCustomerDetails({
          ...customerData,
          orders_count: ordersCount,
          total_spent: totalSpent
        });
      } catch (error) {
        console.error('Error fetching customer details:', error);
        setCustomerDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerDetails();
  }, [customerId]);

  // Function to handle customer selection
  const handleSelectCustomer = (id: string, name: string) => {
    console.log(`Selected customer: ${id} - ${name}`);
    // This function can be expanded based on your needs
  };

  return { customerDetails, loading, handleSelectCustomer };
};
